<script setup lang="ts">
import usePoolStore from '~~/app/store/pool'
import type { PoolStationEvse } from '~~/interfaces/station'
import type { PropType } from 'vue'
import { PoolRealtimeState } from '#cmui/types'
import type { PoolStationConnector } from '#cmui/types'

const props = defineProps({
    connector: {
        type: Object as PropType<PoolStationConnector>,
        required: true
    },
    stationId: {
        type: Number,
        required: true
    },
    evse: {
        type: Object as PropType<PoolStationEvse>,
        required: true
    }
})

const poolStore = usePoolStore()

const isOwnerOrOperatorNameFilled = computed<boolean>(
    () => !!(poolStore.getOwnerName || props.connector?.operatorName)
)

const getOperatorName = computed<string>(
    () => props.connector?.operatorName || ''
)

const getEvse = computed<string>(
    () => props.evse.defaultEvseForCharging?.toString() || ''
)

const onConnectorChange = (event: Event) => {
    poolStore.updateConnectorState(
        Number((event.target as HTMLInputElement)?.value) || 0,
        props.stationId,
        props.connector.id
    )
}

const isConnectorAvailable = (connector: PoolStationConnector): number =>
    +(connector.realtimeStatus === PoolRealtimeState.AVAILABLE)
</script>

<template>
    <div
        class="w-full bg-[#FEF3C8] gap-y-1 rounded-lg py-2 px-4 text-xs min-h-50 flex items-center font-medium flex-wrap"
    >
        <div v-if="isOwnerOrOperatorNameFilled">
            <span class="text-[#DB7706]">
                {{ $t('word.operator') }} :&nbsp;
            </span>
            <span v-if="getOperatorName" class="text-[#16A339]">
                {{ getOperatorName }}
            </span>
            <span v-else class="text-[#16A339]">
                {{ poolStore.getOwnerName }}
            </span>
            <span class="text-[#DB7706]">&nbsp;/&nbsp;</span>
        </div>
        <div v-if="getEvse">
            <span class="text-[#DB7706]">EVSE :&nbsp;</span>
            <span v-if="evse.defaultEvseForCharging" class="text-[#91400D]">
                {{ evse.defaultEvseForCharging }}
            </span>
            <span class="text-[#DB7706]">&nbsp;-&nbsp;</span>
        </div>
        <div>
            <span class="text-[#DB7706]">
                {{ $t('word.connector') }} :&nbsp;
            </span>
            <span class="text-[#91400D]">{{ connector.id }}</span>
            <span class="text-[#DB7706]">&nbsp;/&nbsp;</span>
        </div>
        <div>
            <span class="text-[#91400D]">{{ $t('word.state') }}&nbsp;</span>
            <select
                class="ml-1 rounded-lg p-1 border-1 border-gray-l-300"
                :value="isConnectorAvailable(connector)"
                @change="onConnectorChange"
            >
                <option value="0">{{ $t('plug.state.out_of_service') }}</option>
                <option value="1">{{ $t('plug.state.functional') }}</option>
            </select>
        </div>
    </div>
</template>
